// packages/charts/src/seriesColors.ts
//
// The colour of every series a dashboard chart draws (ADR-0476). Three
// layers, first hit wins: the block's `chart_colors.overrides`, the
// dashboard's pins (`series_colors`), then the block's palette assigned BY
// VALUE, so a filter or a re-rank no longer moves "Facebook" from blue to
// orange.
//
// Pure: no React, no DOM. `seriesRefsOf` says which series a chart draws (the
// Format pane lists exactly those); `resolveSeriesColors` turns the layers
// into one `Map<series_ref, hex>`.
//
// The Python twin of the grammar is `apps/backend/api/schemas/chart_colors.py`.
// Every stored value is UNTRUSTED here: a version restore and a dashboard copy
// move specs without the write validators (`apply_tree`), so `readChartColors`
// and `readSeriesPins` drop anything outside the grammar before a colour can
// reach ECharts or a swatch `style`. That filter is not redundant with the
// write checks (ADR-0476 §5); do not remove it.

import { isKpiPictureChartType } from "./kpiPictures";
import { sankeyEdges } from "./sankey";
import { SCATTER_NOT_SET_COLOR, groupScatterSeries, placeScatterRows } from "./scatterSeries";
import { DEFAULT_CHART_COLORS } from "./colors";
import { funnelSteps, sortAndTrimDateRows } from "./drawnRows";
import { sparklineHasSignal } from "./sparkline/signal";

/** `m:<metric_key>`, `d:<raw value>` or `r:<role>`. */
export type SeriesRef = string;

export type ChartPaletteKind = "account" | "theme" | "custom";

/** A stored colour: a theme slot or a lowercase `#rrggbb`. */
export type SeriesColor = string;

export type ChartColors = {
  palette: ChartPaletteKind;
  /** Only with `palette: "custom"`, 2..12 colours. */
  custom: SeriesColor[] | null;
  overrides: ReadonlyMap<SeriesRef, SeriesColor>;
};

/** The structural colours of one chart. Chart-level only: a dashboard pin
 *  with an `r:` ref is refused (the backend) and dropped (here). */
export const SERIES_ROLES = ["increase", "decrease", "total", "other", "not_set", "target"] as const;
export type SeriesRole = (typeof SERIES_ROLES)[number];

/** The literal labels the pivot writes for its two buckets
 *  (`chart_formatter._DEFAULT_OTHER_LABEL` / `_NOT_SET_LABEL`). A real value
 *  with that text shares the bucket's colour, as it already shares its series
 *  (ADR-0476, Negative). */
export const OTHER_LABEL = "Other";
export const NOT_SET_LABEL = "(not set)";

export const MAX_SERIES_REF_CHARS = 200;
export const MAX_DASHBOARD_PINS = 500;
export const MAX_CHART_OVERRIDES = 200;
export const MIN_CUSTOM_PALETTE = 2;
export const MAX_CUSTOM_PALETTE = 12;

const PALETTE_SLOT_RE = /^palette_[1-8]$/;
const CUSTOM_COLOR_RE = /^#[0-9a-f]{6}$/;
// Any C0/C1 control character or DEL, as the backend's `_CONTROL_RE`.
// eslint-disable-next-line no-control-regex
const CONTROL_RE = /[\x00-\x1f\x7f-\x9f]/;
const CHART_REF_RE = /^[mdr]:/;
const PIN_REF_RE = /^[md]:/;
const PALETTE_KINDS: ReadonlySet<string> = new Set<ChartPaletteKind>(["account", "theme", "custom"]);

/** `value` as a stored series colour, or null. */
export function seriesColorOrNull(value: unknown): SeriesColor | null {
  return typeof value === "string" && (PALETTE_SLOT_RE.test(value) || CUSTOM_COLOR_RE.test(value)) ? value : null;
}

/** `value` as a series ref, or null. `roles: false` for a dashboard pin.
 *  The length is counted in code points, as Python's `len` counts it. */
export function seriesRefOrNull(value: unknown, { roles }: { roles: boolean }): SeriesRef | null {
  if (typeof value !== "string") return null;
  const length = [...value].length;
  if (length < 3 || length > MAX_SERIES_REF_CHARS) return null;
  if (CONTROL_RE.test(value)) return null;
  return (roles ? CHART_REF_RE : PIN_REF_RE).test(value) ? value : null;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** Own enumerable entries only: a stored `{"__proto__": …}` (JSON.parse makes
 *  it an own key) is an entry like any other, never the prototype. */
function colorEntries(
  value: unknown,
  { roles, max }: { roles: boolean; max: number },
): Map<SeriesRef, SeriesColor> {
  const out = new Map<SeriesRef, SeriesColor>();
  if (!isPlainObject(value)) return out;
  for (const [key, color] of Object.entries(value)) {
    if (out.size >= max) break;
    const ref = seriesRefOrNull(key, { roles });
    const checked = seriesColorOrNull(color);
    if (ref !== null && checked !== null) out.set(ref, checked);
  }
  return out;
}

/** A block's stored `chart_colors`, every entry outside the grammar dropped.
 *  Absent or unreadable is the default: the account palette, no overrides. */
export function readChartColors(value: unknown): ChartColors {
  if (!isPlainObject(value)) return { palette: "account", custom: null, overrides: new Map() };
  const overrides = colorEntries(value.overrides, { roles: true, max: MAX_CHART_OVERRIDES });
  const custom = Array.isArray(value.custom)
    ? value.custom.map(seriesColorOrNull).filter((c): c is SeriesColor => c !== null).slice(0, MAX_CUSTOM_PALETTE)
    : [];
  const kind = typeof value.palette === "string" && PALETTE_KINDS.has(value.palette) ? (value.palette as ChartPaletteKind) : "account";
  if (kind === "custom") {
    // Fewer than two readable colours is not a palette: fall back to the account.
    return custom.length >= MIN_CUSTOM_PALETTE
      ? { palette: "custom", custom, overrides }
      : { palette: "account", custom: null, overrides };
  }
  return { palette: kind, custom: null, overrides };
}

/** A dashboard's stored pins, every entry outside the grammar dropped (an
 *  `r:` ref included: a role is never dashboard-wide). */
export function readSeriesPins(value: unknown): Map<SeriesRef, SeriesColor> {
  return colorEntries(value, { roles: false, max: MAX_DASHBOARD_PINS });
}

/** 32-bit FNV-1a over the UTF-16 code units. Deterministic across browsers and
 *  Node, so a value draws the same colour everywhere. */
export function fnv1a(text: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < text.length; i += 1) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash >>> 0;
}

/** Only what `seriesRefsOf` reads of a display spec. */
export type SeriesRefSpec = {
  chart_type?: string | null;
  chart_index_by?: string | null;
  y_keys?: string[] | null;
  rows?: Record<string, unknown>[] | null;
  breakdown_key?: string | null;
  dimension_keys?: string[] | null;
  /** Result column (or metric key) → display label: a metric funnel's step
   *  names are these labels (`chart_formatter.funnel_rows`). */
  labels?: Record<string, string> | null;
  /** A KPI picture's plan, stamped per response (`readPictureTarget`). */
  picture_target?: { value?: unknown } | null;
  /** The comparison window: a funnel with nothing to show in either window
   *  draws no step (`funnelSteps`). Also trimmed with the rows. */
  comparison_rows?: unknown;
  target_rows?: unknown;
  /** A time axis keeps its trailing empty periods (`sortAndTrimDateRows`). */
  time_axis?: unknown;
  /** A metric card's sparkline: its one series is listed only when drawn. */
  sparkline_points?: (number | null)[] | null;
};

const PIE_LIKE = new Set(["pie", "donut"]);
const WATERFALLS = new Set(["waterfall", "bridge", "metric_waterfall"]);
const SCATTERS = new Set(["scatter", "bubble"]);
/** Chart types that draw no series: their colour is conditional formatting
 *  (tables, the pivot) or their own lines (receipt; step 5's
 *  `colour_contract` decides whether it takes roles). A metric card draws one,
 *  its sparkline (ADR-0476 §7, amended 2026-10-19). */
const NO_SERIES = new Set([
  "table",
  "pivot_table",
  "kpi_period_table",
  "funnel_explorer",
  "funnel_step_detail",
  "receipt",
]);

const asText = (value: unknown): string => (value === null || value === undefined ? "" : String(value));

/** The ref of one drawn dimension value: the pivot's two buckets are roles. */
function valueRef(raw: string): SeriesRef {
  if (raw === "" || raw === NOT_SET_LABEL) return "r:not_set";
  if (raw === OTHER_LABEL) return "r:other";
  return `d:${raw}`;
}

// ---- Per drawn item ---------------------------------------------------------
// The builder asks these for the ref of the ONE item it is painting (a slice,
// a tile, a node, a series), and `seriesRefsOf` below lists the same refs for
// the whole chart. One function per kind of item, so the pane's row and the
// drawn item can never be keyed apart.

/** A dimension value as drawn by a pie slice, a treemap tile or a funnel step
 *  by value: the RAW cell, never its formatted label. */
export function dimensionValueRef(raw: unknown): SeriesRef {
  return valueRef(asText(raw));
}

/** One series of the shared cartesian loop: after a breakdown pivot the key
 *  IS a value of the breakdown dimension, otherwise it is a measure. */
export function cartesianSeriesRef(yKey: string, breakdownKey: string | null | undefined): SeriesRef {
  return breakdownKey ? valueRef(yKey) : `m:${yKey}`;
}

/** A sankey node id (`s:`/`t:` + the value, or a side's folded "Other",
 *  `sankey.ts`). The side is layout, not identity: NL as a source and NL as a
 *  target are one value and take one colour. */
export function sankeyNodeRef(id: string): SeriesRef {
  return id.endsWith("\u0000other") ? "r:other" : valueRef(id.slice(2));
}

/** One series of a scatter with a breakdown (`groupScatterSeries`). */
export function scatterGroupRef(group: { name: string; notSet: boolean }): SeriesRef {
  return group.notSet ? "r:not_set" : valueRef(group.name);
}

/**
 * One funnel step, or null for a step the funnel names with nothing.
 *
 * A metric funnel's steps are measures named by their label
 * (`chart_formatter.funnel_rows` writes `stage: labels[column]`), so a step
 * maps back to its key: renaming a metric must not lose its colour, and a
 * pin on a dimension value spelled "Sessions" must not colour the step.
 */
export function funnelStepRef(
  labels: Record<string, string> | null | undefined,
  indexKey: string | null | undefined,
  row: Record<string, unknown>,
): SeriesRef | null {
  // An absent index reads as "", as `seriesRefsOf` reads `chart_index_by`.
  const xKey = indexKey ?? "";
  if (xKey !== "" && xKey !== "stage") return valueRef(asText(row[xKey]));
  const stage = asText(row[xKey || "stage"]);
  if (stage === "") return null;
  for (const [key, label] of Object.entries(labels ?? {})) {
    if (label === stage) return `m:${key}`;
  }
  return `m:${stage}`;
}

function unique(refs: Iterable<SeriesRef>): SeriesRef[] {
  return [...new Set(refs)];
}

function sankeyRefs(spec: SeriesRefSpec, xKey: string): SeriesRef[] {
  const second = spec.dimension_keys?.[1];
  const rows = spec.rows ?? [];
  if (!second) return [];
  const { edges } = sankeyEdges({
    rows,
    yKeys: spec.y_keys ?? [],
    categories: rows.map((r) => asText(r[xKey])),
    secondCategories: rows.map((r) => asText(r[second])),
  });
  return unique(edges.flatMap((e) => [e.source, e.target]).map(sankeyNodeRef));
}

function scatterRefs(spec: SeriesRefSpec, breakdownKey: string): SeriesRef[] {
  // The builder's own placement and grouping, so only the rows it can place
  // count towards which series survive the cap.
  const placed = placeScatterRows(spec.rows ?? [], spec.y_keys ?? [], breakdownKey, () => null);
  return unique(groupScatterSeries(placed).groups.map(scatterGroupRef));
}

function funnelRefs(spec: SeriesRefSpec, xKey: string, rows: Record<string, unknown>[]): SeriesRef[] {
  // The builder's own refusal: nothing to show, no step drawn, none listed.
  const comparisonRows = Array.isArray(spec.comparison_rows) ? spec.comparison_rows : [];
  if (funnelSteps(rows, spec.y_keys?.[0], comparisonRows, xKey).scale <= 0) return [];
  return unique(
    rows.map((r) => funnelStepRef(spec.labels, xKey, r)).filter((ref): ref is SeriesRef => ref !== null),
  );
}

/** A treemap draws nothing when a value is negative or the total is not
 *  positive (`buildTreemapOption`), so it lists nothing then either. */
function treemapRefs(rows: Record<string, unknown>[], xKey: string, yKey: string | undefined): SeriesRef[] {
  if (!yKey) return [];
  const values = rows.map((r) => {
    const n = typeof r[yKey] === "number" ? (r[yKey] as number) : Number(r[yKey]);
    return Number.isFinite(n) ? n : 0;
  });
  if (values.some((v) => v < 0) || values.reduce((sum, v) => sum + v, 0) <= 0) return [];
  return unique(rows.map((r) => valueRef(asText(r[xKey]))));
}

/**
 * The series a chart draws, as refs, in the order the Format pane lists them.
 * Reads the DISPLAY spec (after the breakdown pivot, the caps and the metric
 * chips), so the pane can never list a series the chart did not draw.
 *
 * A drawn value outside the stored grammar (a tab in it, over 200 characters)
 * is still listed and still coloured by value; it cannot be pinned, so the
 * pane asks `seriesRefOrNull` before it offers a swatch.
 */
export function seriesRefsOf(input: SeriesRefSpec): SeriesRef[] {
  const chartType = (input.chart_type ?? "").toLowerCase();
  const xKey = input.chart_index_by ?? "";
  // The rows the builder draws: a date index sorted, its trailing empty
  // periods stripped (`drawnRows.ts`, shared with the builder).
  const spec = sortAndTrimDateRows(input, xKey).spec;
  const yKeys = (spec.y_keys ?? []).filter((k) => typeof k === "string" && k !== "");
  const rows = spec.rows ?? [];

  if (NO_SERIES.has(chartType)) return [];
  // Only the headline measure: a secondary line's own key is no series.
  if (chartType === "metric_card") return yKeys[0] && sparklineHasSignal(input.sparkline_points) ? [`m:${yKeys[0]}`] : [];
  if (isKpiPictureChartType(chartType)) {
    if (!yKeys[0]) return [];
    const target = spec.picture_target?.value;
    return typeof target === "number" && Number.isFinite(target) ? [`m:${yKeys[0]}`, "r:target"] : [`m:${yKeys[0]}`];
  }
  if (WATERFALLS.has(chartType)) return ["r:increase", "r:decrease", "r:total"];
  if (chartType === "sankey") return sankeyRefs(spec, xKey);
  if (PIE_LIKE.has(chartType)) return unique(rows.map((r) => valueRef(asText(r[xKey]))));
  if (chartType === "funnel") return funnelRefs(spec, xKey, rows);
  if (chartType === "treemap") return treemapRefs(rows, xKey, yKeys[0]);
  if (SCATTERS.has(chartType)) {
    return spec.breakdown_key ? scatterRefs(spec, spec.breakdown_key) : yKeys[0] ? [`m:${yKeys[0]}`] : [];
  }
  // Cartesian: after a breakdown pivot the keys ARE the values of one
  // dimension; otherwise every key is a measure.
  return unique(yKeys.map((k) => (spec.breakdown_key ? valueRef(k) : `m:${k}`)));
}

/** The palettes a colour can come from on this dashboard. */
export type SeriesPalettes = {
  /** The account chart palette (hex), resolved: a tile draws only once it is
   *  (ADR-0476 §4), or `hash mod N` would jump on load. */
  account: readonly string[];
  /** The dashboard's 8 theme colours, index 0 = `palette_1`, or null when the
   *  dashboard has none in scope. */
  theme: readonly string[] | null;
};

export type SeriesColorLayers = {
  /** The block's stored `chart_colors`, unread. */
  chartColors?: unknown;
  /** The dashboard's stored pins, unread. */
  pins?: unknown;
};

/** What a dashboard tile hands `buildEchartsOption` (its `colorLayers`): the
 *  stored layers plus the palettes they resolve against. Absent, the builder
 *  draws exactly what it drew before ADR-0476 (chat, reports, MCP Apps). */
export type ChartColorLayers = SeriesColorLayers & { palettes: SeriesPalettes };

const hexOrNull = (value: unknown): string | null =>
  typeof value === "string" && /^#[0-9a-fA-F]{6}$/.test(value) ? value.toLowerCase() : null;

function checkedPalette(colors: readonly unknown[] | null | undefined): string[] {
  return (colors ?? []).map(hexOrNull).filter((c): c is string => c !== null);
}

/** The pivot's two buckets are not values: with nothing set they draw a
 *  fixed neutral, never a palette colour a value would then share. */
const BUCKET_DEFAULTS: ReadonlyMap<SeriesRef, string> = new Map([
  ["r:other", "#adb5bd"],
  ["r:not_set", SCATTER_NOT_SET_COLOR],
]);

const THEME_SLOTS = 8;
const ANY_REF_RE = /^[mdr]:/;

/** A stored colour as hex. A slot reads the dashboard theme, or the account
 *  palette when there is none ("the palette in force", ADR-0351). */
function colorHex(color: SeriesColor, palettes: SeriesPalettes): string | null {
  const slot = PALETTE_SLOT_RE.test(color) ? Number(color.slice("palette_".length)) - 1 : null;
  if (slot === null) return hexOrNull(color);
  return hexOrNull((palettes.theme ?? palettes.account)[slot]);
}

function palettesInForce(palettes: SeriesPalettes): SeriesPalettes {
  const account = checkedPalette(palettes.account);
  return {
    account: account.length > 0 ? account : DEFAULT_CHART_COLORS,
    // A theme is its 8 slots or nothing: a partial one would leave a slot
    // pin unresolvable instead of falling back to the account palette.
    theme: palettes.theme && checkedPalette(palettes.theme).length === THEME_SLOTS ? checkedPalette(palettes.theme) : null,
  };
}

function chosenPalette(chart: ChartColors, inForce: SeriesPalettes): string[] {
  if (chart.palette === "custom" && chart.custom) {
    const custom = chart.custom.map((c) => colorHex(c, inForce)).filter((c): c is string => c !== null);
    if (custom.length >= MIN_CUSTOM_PALETTE) return custom;
  }
  if (chart.palette === "theme" && inForce.theme) return [...inForce.theme];
  return [...inForce.account];
}

/**
 * The palette this chart draws from: the block's `chart_colors.palette`
 * (account, the dashboard theme, or its own list), resolved. The by-value
 * assignment probes it, and the builder takes its positional picks for what
 * is left out of the map (a measure or a role with nothing set) from it too,
 * so choosing "Theme colours" also recolours a chart of measures.
 */
export function chartPaletteOf(layers: SeriesColorLayers, palettes: SeriesPalettes): string[] {
  return chosenPalette(readChartColors(layers.chartColors), palettesInForce(palettes));
}

/**
 * The colour of each series in `refs`: an override beats a pin, a pin beats
 * the palette; a layer whose colour does not resolve falls through to the
 * next. Only `d:` refs take a palette colour BY VALUE; "Other" and "(not set)"
 * take a fixed neutral; a measure or another role with nothing set is left out
 * of the map, so the builder keeps today's pick for it (a measure's order is
 * the editor's own, a role has its own default).
 *
 * By value: `fnv1a(ref) mod N`. Every pinned or overridden colour of THIS
 * chart that is a palette entry reserves that index first, so "pin Facebook,
 * rest automatic" never draws Google in Facebook's blue. A collision moves the
 * later ref (in ref order, never by rank) to the next free index; above N
 * every index is taken and colours repeat at the value's own index.
 */
export function resolveSeriesColors(
  refs: readonly SeriesRef[],
  layers: SeriesColorLayers,
  palettes: SeriesPalettes,
): Map<SeriesRef, string> {
  const chart = readChartColors(layers.chartColors);
  const pins = readSeriesPins(layers.pins);
  const inForce = palettesInForce(palettes);
  const palette = chosenPalette(chart, inForce);

  const resolved = new Map<SeriesRef, string>();
  const unpinned: SeriesRef[] = [];
  for (const ref of unique(refs)) {
    if (typeof ref !== "string" || !ANY_REF_RE.test(ref)) continue;
    const override = chart.overrides.get(ref);
    const pin = ref.startsWith("r:") ? undefined : pins.get(ref);
    const hex =
      (override === undefined ? null : colorHex(override, inForce)) ??
      (pin === undefined ? null : colorHex(pin, inForce)) ??
      BUCKET_DEFAULTS.get(ref) ??
      null;
    if (hex !== null) resolved.set(ref, hex);
    else if (ref.startsWith("d:")) unpinned.push(ref);
  }

  const taken = new Set<number>();
  for (const hex of resolved.values()) {
    palette.forEach((entry, i) => {
      if (entry === hex) taken.add(i);
    });
  }
  const n = palette.length;
  for (const ref of unpinned.sort((a, b) => (a < b ? -1 : a > b ? 1 : 0))) {
    const home = fnv1a(ref) % n;
    let index = home;
    for (let step = 0; step < n && taken.has(index); step += 1) index = (home + step + 1) % n;
    if (taken.has(index)) index = home;
    taken.add(index);
    resolved.set(ref, palette[index]);
  }
  return resolved;
}

/**
 * Everything a dashboard tile's builder needs to paint by ref: the resolved
 * map over the series this chart draws, and the palette in force for the
 * positional picks of whatever the map leaves out.
 */
export function resolveChartColors(
  spec: SeriesRefSpec,
  layers: ChartColorLayers,
): { colors: Map<SeriesRef, string>; palette: string[] } {
  return {
    colors: resolveSeriesColors(seriesRefsOf(spec), layers, layers.palettes),
    palette: chartPaletteOf(layers, layers.palettes),
  };
}

// ---- Positional picks (the drawing before ADR-0476) -------------------------
// The ONLY place on the dashboard path that indexes a palette. A chart
// without colour layers (chat, a report, an MCP App) and a measure or role
// with nothing set on a dashboard keep today's picks, and they get them from
// here; `__tests__/paletteIndexGuard.test.ts` fails on a `colors[`/`palette[`
// index anywhere else in the builder modules, so a new colour site has to
// choose between a ref and a positional pick on purpose.

/** `palette[index]`, wrapping round: what a series at that position drew.
 *  `undefined` for an empty palette, which is what the old inline index
 *  produced too; callers that cannot take it say so at the call. */
export function positionalColor(palette: readonly string[], index: number): string | undefined {
  return palette[index % palette.length];
}

/**
 * The colour of the series at `index` among `seriesCount`: its distributed
 * pick (`selectDistributedColors`) or, past the picks, its position.
 */
export function distributedPick(palette: string[], seriesCount: number): (index: number) => string | undefined {
  const picks = selectDistributedColors(palette, seriesCount);
  return (index) => picks[index] ?? positionalColor(palette, index);
}

/**
 * A step waterfall's three bar colours, taken from the tile's chart palette so
 * the chart follows the dashboard's style like every other bar chart: the
 * levels, the steps up and the steps down, spread over the palette the way
 * three series would be (`selectDistributedColors`). Green/red/grey was a
 * second colour scheme on a chart that otherwise matches nothing around it
 * (Arjen, 2026-10-17). Up and down stay apart by tone, and the bar's position
 * and the tooltip's sign say which way a step goes.
 */
export function stepWaterfallColors(colors: string[]): { level: string; up: string; down: string } {
  const picks = selectDistributedColors(colors.length > 0 ? colors : DEFAULT_CHART_COLORS, 3);
  const level = picks[0];
  const up = picks[1] ?? level;
  return { level, up, down: picks[2] ?? up };
}

/**
 * Select well-distributed colors from a palette when there are few series.
 * For ≤3 series, picks darker colors spread across the palette for better contrast,
 * especially important for monochrome palettes where adjacent colors are similar.
 *
 * Examples with 10-color palette (0=darkest, 9=lightest):
 * - 1 series: pick index 2 (dark but not darkest)
 * - 2 series: pick indices 1, 4 (dark and medium)
 * - 3 series: pick indices 1, 3, 5 (spread in darker half)
 * - 4+ series: use sequential colors from start
 */
export function selectDistributedColors(palette: string[], seriesCount: number): string[] {
  if (seriesCount <= 0 || palette.length === 0) return [];
  if (seriesCount > 3 || palette.length <= seriesCount) {
    // For 4+ series or small palettes, use sequential colors
    return palette.slice(0, seriesCount);
  }

  const len = palette.length;

  if (seriesCount === 1) {
    // Single series: pick 3rd color (index 2) - dark but not darkest
    const idx = Math.min(2, len - 1);
    return [palette[idx]];
  }

  if (seriesCount === 2) {
    // Two series: pick darker colors with good contrast
    // For 10-color palette: indices 1 and 4
    const first = Math.min(1, len - 1);
    const second = Math.min(Math.floor(len * 0.4), len - 1);
    return [palette[first], palette[second]];
  }

  // Three series: spread in the darker half of the palette
  // For 10-color palette: indices 1, 3, 5
  const step = 2;
  return Array.from({ length: seriesCount }, (_, i) =>
    palette[Math.min(1 + i * step, len - 1)]
  );
}
